import React, { useEffect, useState, useCallback, useRef } from "react";
import { getDocDownloadUrlAPI } from "../../common/services";
import PageLoader from "../page-loader";
import Button from "../button";

const OFFICE_URL_EXPIRY_SECONDS = 900; // 15 minutes

const activeCellParam = (sheet, row) => (
  sheet ? `&ActiveCell=${encodeURIComponent(`'${sheet.replace(/'/g, "''")}'!A${row || 1}`)}` : ""
);

const OfficeViewer = ({ file, pageNumber, sheet, row, hideDownloadButton = false }) => {
  const [presignedUrl, setPresignedUrl] = useState("");
  const [hasError, setHasError] = useState(false);
  const refreshTimerRef = useRef(null);

  const fetchPresignedUrl = useCallback(async () => {
    if (hasError || !file) return;

    try {
      const response = await getDocDownloadUrlAPI(file, OFFICE_URL_EXPIRY_SECONDS);
      const { url, expiresIn } = response.data;
      setPresignedUrl(url);
      setHasError(false);

      // Clear any existing timer
      if (refreshTimerRef.current) {
        clearTimeout(refreshTimerRef.current);
      }

      // Refresh URL 1 minute before it expires (expiresIn is in seconds)
      const refreshDelay = (expiresIn - 60) * 1000;
      if (refreshDelay > 0) {
        refreshTimerRef.current = setTimeout(() => {
          fetchPresignedUrl();
        }, refreshDelay);
      }
    } catch (error) {
      console.error("Failed to fetch presigned URL for Office document", error);
      setHasError(true);
    }
  }, [file, hasError]);

  const onDownloadFile = useCallback(()=>{
    if (!presignedUrl) return;
    window.open(presignedUrl, '_blank');
  }, [presignedUrl])

  useEffect(() => {
    setPresignedUrl("");
    setHasError(false);
  }, [file]);

  useEffect(() => {
    fetchPresignedUrl();

    return () => {
      if (refreshTimerRef.current) {
        clearTimeout(refreshTimerRef.current);
      }
    };
  }, [fetchPresignedUrl]);

  if (!presignedUrl) {
    return <PageLoader />;
  }

  const encoded = encodeURIComponent(presignedUrl);

  // Page support is approximate
  const pageParam = pageNumber ? `&wdStartOn=${pageNumber}` : "";

  const src = `https://view.officeapps.live.com/op/embed.aspx?src=${encoded}${pageParam}${activeCellParam(sheet, row)}`;

  return (
    <div className="bimmatch-office-viewer-container">
      <iframe
        title="Office Viewer"
        src={src}
        width="100%"
        height="100%"
        className="office-viewer-iframe"
      />

      {!hideDownloadButton && (
        <Button className="download-btn-container" onClick={onDownloadFile}>
          <>
            <img src="/images/icon/download.svg" alt="download" className="download-btn" />
            Download
          </>
        </Button>
      )}
    </div>
  );
};

export default OfficeViewer;
